import * as React from "react";
import type { ModalComponentProps } from "../../../types/modal.types";
import ConfirmModal from "../confirm.modal";

interface DeviceEventPeriodModalProps extends ModalComponentProps {
  eventFrom: string;
  eventTo: string;
  onSave: (eventFrom: string, eventTo: string) => void;
}

const DATE_TIME_PATTERN = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/;

const formatDateTimeInput = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 12);
  if (digits.length <= 4) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  if (digits.length <= 8) return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6)}`;
  if (digits.length <= 10) {
    return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)} ${digits.slice(8)}`;
  }
  return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)} ${digits.slice(8, 10)}:${digits.slice(10)}`;
};

const toTime = (value: string) => new Date(value.replace(" ", "T")).getTime();
const isValidDateTime = (value: string) => DATE_TIME_PATTERN.test(value) && !Number.isNaN(toTime(value));

export default function DeviceEventPeriodModal({
  id,
  onClose,
  eventFrom: initialEventFrom,
  eventTo: initialEventTo,
  onSave,
}: DeviceEventPeriodModalProps) {
  const [eventFrom, setEventFrom] = React.useState(initialEventFrom);
  const [eventTo, setEventTo] = React.useState(initialEventTo);
  const invalidFormat = !isValidDateTime(eventFrom) || !isValidDateTime(eventTo);
  const invalidOrder = !invalidFormat && toTime(eventFrom) > toTime(eventTo);

  return (
    <ConfirmModal
      id={id}
      onClose={onClose}
      title="이벤트 기간 수정"
      className="w-[560px]"
      rightButton={{
        title: "저장",
        disabled: invalidFormat || invalidOrder,
        onClick: () => {
          onSave(eventFrom, eventTo);
          onClose?.();
        },
      }}
    >
      <div className="mt-5 flex items-end gap-3">
        <label className="flex flex-1 flex-col gap-2 text-[13px] text-black/60">
          시작 일시
          <input
            type="text"
            value={eventFrom}
            placeholder="YYYY-MM-DD HH:mm"
            inputMode="numeric"
            maxLength={16}
            className="h-11 rounded-[6px] bg-black/[0.04] px-3 text-[14px] text-black outline-none focus:ring-2 focus:ring-[#1976D2]/30"
            onChange={(event) => setEventFrom(formatDateTimeInput(event.target.value))}
          />
        </label>
        <span aria-hidden="true" className="pb-3 text-black/35">~</span>
        <label className="flex flex-1 flex-col gap-2 text-[13px] text-black/60">
          종료 일시
          <input
            type="text"
            value={eventTo}
            placeholder="YYYY-MM-DD HH:mm"
            inputMode="numeric"
            maxLength={16}
            className="h-11 rounded-[6px] bg-black/[0.04] px-3 text-[14px] text-black outline-none focus:ring-2 focus:ring-[#1976D2]/30"
            onChange={(event) => setEventTo(formatDateTimeInput(event.target.value))}
          />
        </label>
      </div>
      {(invalidFormat || invalidOrder) && (
        <p className="mt-2 text-[12px] text-[#FF3B30]">
          {invalidOrder
            ? "시작 일시는 종료 일시보다 늦을 수 없습니다."
            : "YYYY-MM-DD HH:mm 형식으로 입력해 주세요."}
        </p>
      )}
    </ConfirmModal>
  );
}
